import React, { useState, useEffect, useMemo } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { disputeService } from '../services/disputeService';

interface DisputeRankingDrawerProps {
  sessionId: number;
  itemKey: number | -1 | null; // -1 = grupo agregado; 1..N = item específico; null = fechado
  itemDescription?: string;
  onClose: () => void;
}

type TabKey = 'propostas' | 'melhores' | 'todos' | 'ranking';

const fmtCurrency4 = (v: any) => {
  const n = typeof v === 'number' ? v : (v ? parseFloat(String(v).replace(/\./g, '').replace(',', '.')) : null);
  if (n === null || isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });
};
const fmtDateTime = (s: any) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }); } catch { return String(s); }
};

/**
 * Drawer lateral mostrando os 3 painéis de lances do ComprasNet:
 *  - Propostas iniciais (estático, ~30s refresh)
 *  - Melhores valores por fornecedor (cronológico)
 *  - Todos os lances (histórico completo)
 *
 * Lê dados de `rankingData` JSONB da sessão, populado pela extension via
 * fetch direto dos endpoints internos do Angular do ComprasNet.
 */
export function DisputeRankingDrawer({ sessionId, itemKey, itemDescription, onClose }: DisputeRankingDrawerProps) {
  const open = itemKey !== null;
  const [session, setSession] = useState<any | null>(null);
  const [tab, setTab] = useState<TabKey>('ranking');
  const [page, setPage] = useState(0);
  const [todosUseAggregate, setTodosUseAggregate] = useState(false);
  const PAGE_SIZE = 10;

  // Poll session pra atualizar ranking em tempo real
  useEffect(() => {
    if (!open) return;
    let alive = true;
    let timer: any;
    const tick = async () => {
      try {
        const data = await disputeService.getSession(sessionId);
        if (alive) setSession(data);
      } catch {}
      if (alive) timer = setTimeout(tick, 1500);
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [open, sessionId]);

  useEffect(() => { setPage(0); }, [tab, itemKey]);
  useEffect(() => { setTodosUseAggregate(false); }, [itemKey, tab]);
  // R1.7: removido auto-switch pra Ranking — era frustrante porque sobrescrevia a aba
  // que o usuário clicou. Tab default fica em 'melhores' (definido no useState inicial).

  const ranking = session?.rankingData || {};
  const k = String(itemKey);
  // Fallback: se item específico não tem dados (ou só tem 1 entrada = só minha proposta inicial),
  // cai pro agregado do grupo (-1) que tem todos os fornecedores.
  const itemHasUsefulData = (bucket: any) => bucket && Array.isArray(bucket.items) && bucket.items.length > 1;

  const propostas = itemHasUsefulData(ranking.propostasIniciais?.[k]) ? ranking.propostasIniciais[k] : ranking.propostasIniciais?.['-1'];
  const propostasIsAggregate = !itemHasUsefulData(ranking.propostasIniciais?.[k]) && !!ranking.propostasIniciais?.['-1'];
  const melhores = itemHasUsefulData(ranking.lancesPorParticipante?.[k]) ? ranking.lancesPorParticipante[k] : ranking.lancesPorParticipante?.['-1'];
  const melhoresIsAggregate = !itemHasUsefulData(ranking.lancesPorParticipante?.[k]) && !!ranking.lancesPorParticipante?.['-1'];
  // R1.9: "Todos os lances" — só cai pro agregado quando user clica no botão fallback
  const todosOwn = ranking.todosLances?.[k];
  const todosAgg = ranking.todosLances?.['-1'];
  const todos = todosUseAggregate ? todosAgg : todosOwn;
  const todosHasFallback = !todosOwn && !!todosAgg;
  // Ranking Final: a captura Firefox grava `todasPropostas[k]` como ARRAY DIRETO
  // [{numero, propostasItem:[...]}] (não {items}). Normaliza pra {items:[participantes]}.
  const normRankingFinal = (entry: any): { items: any[]; capturedAt?: string } | null => {
    if (!entry) return null;
    const arr: any[] = Array.isArray(entry) ? entry : Array.isArray(entry.items) ? entry.items : [];
    const items = arr.length && Array.isArray(arr[0]?.propostasItem) ? arr[0].propostasItem : arr;
    return items.length ? { items, capturedAt: !Array.isArray(entry) ? entry.capturedAt : undefined } : null;
  };
  const rankingFinal = normRankingFinal(ranking.todasPropostas?.[k]) || normRankingFinal(ranking.todasPropostas?.['-1']);
  const rankingFinalIsAggregate = !normRankingFinal(ranking.todasPropostas?.[k]) && !!normRankingFinal(ranking.todasPropostas?.['-1']);

  const activeData = useMemo(() => {
    if (tab === 'propostas') return propostas;
    if (tab === 'melhores') return melhores;
    if (tab === 'todos') return todos;
    return rankingFinal;
  }, [tab, propostas, melhores, todos, rankingFinal]);

  const rows = Array.isArray(activeData?.items) ? activeData.items : [];
  const totalPages = Math.ceil(rows.length / PAGE_SIZE) || 1;
  const pageRows = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  // ⚠️ A aba "Melhores valores por fornecedor" é ANÔNIMA — só valores, sem CNPJ
  // (é assim no portal). Até 02/09/2026 nenhuma linha aparecia como nossa e o
  // operador não sabia em que posição estava sem conferir no ComprasNet.
  // O NOSSO valor vem do estado da sessão: no grupo, do nó `tipo:'G'` do
  // em-disputa (melhorValorFornecedor, ao vivo); no item, do lastBid do
  // itemLimits. Casamos por VALOR, que é o único elo disponível.
  const meuValor: number | null = (() => {
    const rd: any = session?.rankingData || {};
    if (itemKey === -1) {
      const gNode = (Array.isArray(rd.emDisputa) ? rd.emDisputa : []).find(
        (r: any) => r?.tipo === 'G' || r?.numero === -1,
      );
      const v = gNode?.melhorValorFornecedor?.valorCalculado ?? gNode?.melhorValorFornecedor?.valorInformado;
      return typeof v === 'number' && v > 0 ? v : null;
    }
    const il = (session?.itemLimits || []).find((x: any) => Number(x.itemNumber) === Number(itemKey));
    const v = il?.lastBid != null ? Number(il.lastBid) : null;
    return v != null && isFinite(v) && v > 0 ? v : null;
  })();
  const ehMeuValor = (v: any) => meuValor != null && typeof v === 'number' && Math.abs(v - meuValor) < 0.005;
  // Posição na lista ordenada (a tabela do portal já vem do menor pro maior).
  const minhaPosicao = (() => {
    if (tab !== 'melhores' || meuValor == null || rows.length === 0) return null;
    const vals = rows
      .map((r: any) => r?.valor?.valorCalculado ?? r?.valor?.valorInformado)
      .filter((v: any) => typeof v === 'number' && v > 0)
      .sort((a: number, b: number) => a - b);
    const idx = vals.findIndex((v: number) => Math.abs(v - meuValor) < 0.005);
    return idx >= 0 ? { pos: idx + 1, total: vals.length } : null;
  })();

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-central-elevado border-l border-central-borda shadow-2xl z-50 flex flex-col">
        <div className="px-6 py-4 border-b border-central-borda flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-slate-500 tracking-wide">
              {itemKey === -1 ? 'GRUPO AGREGADO' : `ITEM ${itemKey}`}
            </div>
            <div className="text-base font-medium text-slate-100 truncate max-w-md">
              {itemDescription || (itemKey === -1 ? 'Total do grupo (todos os fornecedores)' : `Item ${itemKey}`)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-central-elevado rounded text-slate-400 hover:text-slate-200"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-central-borda px-6 flex gap-1">
          {/* Simplificado (21/05): só "Ranking Final" — ele já traz valor inicial,
              valor final, classificação e vencedor de cada fornecedor. As abas de
              lances (por participante / todos) eram captcha-dependentes e redundantes. */}
          {([
            { k: 'ranking', label: 'Ranking Final', count: rankingFinal?.items?.length },
          ] as { k: TabKey; label: string; count?: number }[]).map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-sm border-b-2 transition ${
                tab === t.k
                  ? 'border-blue-500 text-blue-400 font-medium'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.label}
              {typeof t.count === 'number' && <span className="ml-1.5 text-xs text-slate-500">({t.count})</span>}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {!activeData && (
            <div className="text-center text-slate-500 py-12">
              <RefreshCw size={28} className="mx-auto mb-2 opacity-50" />
              <div>Sem dados nesta aba para o item {itemKey}.</div>
              <div className="text-xs mt-1">
                {tab === 'todos' && todosHasFallback
                  ? 'O portal só expõe lances agregados em pregão de grupo na fase Encerrada.'
                  : itemKey !== -1
                    ? 'Tente o "Lances do grupo" no header pra ver dados agregados.'
                    : 'A extension ainda não coletou esse endpoint.'}
              </div>
              {tab === 'todos' && todosHasFallback && (
                <button
                  onClick={() => setTodosUseAggregate(true)}
                  className="mt-4 px-4 py-2 rounded bg-amber-500/20 border border-amber-700/40 text-amber-300 text-sm hover:bg-amber-500/30"
                >
                  Ver lances agregados do grupo →
                </button>
              )}
            </div>
          )}
          {activeData && (tab === 'propostas' ? propostasIsAggregate : tab === 'melhores' ? melhoresIsAggregate : tab === 'todos' ? todosUseAggregate : tab === 'ranking' ? rankingFinalIsAggregate : false) && (
            <div className="mb-2 text-xs text-amber-300/80 bg-amber-500/10 border border-amber-700/30 rounded px-2 py-1 flex items-center justify-between">
              <span>ℹ Exibindo dados <strong>agregados do grupo</strong> (sem dados específicos pra este item).</span>
              {tab === 'todos' && todosUseAggregate && (
                <button
                  onClick={() => setTodosUseAggregate(false)}
                  className="text-xs text-amber-300 hover:text-amber-200 underline"
                >
                  voltar
                </button>
              )}
            </div>
          )}
          {activeData && rows.length === 0 && (
            <div className="text-center text-slate-500 py-12">
              Nenhum registro na aba <strong>{tab}</strong> ainda.
            </div>
          )}
          {activeData && rows.length > 0 && (
            <>
              <div className="text-xs text-slate-500 mb-2">
                Última atualização: {fmtDateTime(activeData.capturedAt)}
                {activeData.totalRows != null && activeData.totalRows !== rows.length && (
                  <span> · exibindo {rows.length} de {activeData.totalRows}</span>
                )}
              </div>
              {minhaPosicao && (
                <div className="mb-2 text-xs bg-emerald-500/10 border border-emerald-700/40 rounded px-2 py-1 text-emerald-300">
                  IPCOM em <strong>{minhaPosicao.pos}º</strong> de {minhaPosicao.total} nesta lista, com{' '}
                  <span className="font-mono">{fmtCurrency4(meuValor)}</span>
                  <span className="text-emerald-300/60"> · a lista do portal é anônima; a linha é reconhecida pelo valor</span>
                </div>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500 border-b border-central-borda">
                    {tab === 'propostas' ? (
                      propostasIsAggregate ? (
                        <>
                          <th className="px-3 py-2 w-10">#</th>
                          <th className="px-3 py-2">Fornecedor</th>
                          <th className="px-3 py-2 text-right">Valor total da proposta</th>
                        </>
                      ) : (
                        <>
                          <th className="px-3 py-2">#</th>
                          <th className="px-3 py-2 text-right">Quantidade</th>
                          <th className="px-3 py-2 text-right">Valor unit.</th>
                        </>
                      )
                    ) : tab === 'ranking' ? (
                      <>
                        <th className="px-3 py-2 text-center w-10">#</th>
                        <th className="px-3 py-2">Fornecedor (CNPJ · UF)</th>
                        <th className="px-3 py-2 text-right">Inicial</th>
                        <th className="px-3 py-2 text-right">Final</th>
                        <th className="px-3 py-2 text-center w-16">ME/EPP</th>
                      </>
                    ) : (
                      <>
                        <th className="px-3 py-2">Data/Hora</th>
                        <th className="px-3 py-2 text-right">Valor {itemKey === -1 ? 'total' : 'unit.'}</th>
                        <th className="px-3 py-2">Origem</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r: any, i: number) => {
                    // Ranking Final tem schema próprio: { classificacao, valores: {...}, participante: {...} }
                    if (tab === 'ranking') {
                      const cls = r.classificacao;
                      const cnpj = r.participante?.identificacao;
                      const nome = r.participante?.nome;
                      const uf = r.participante?.endereco?.uf;
                      const valIni = r.valores?.valorPropostaInicial?.valorCalculado?.valorTotal ?? r.valores?.valorPropostaInicial?.valorInformado;
                      const valFin = r.valores?.valorPropostaInicialOuLances?.valorCalculado?.valorTotal ?? r.valores?.valorPropostaInicialOuLances?.valorInformado;
                      const isMine = cnpj === '06163889000104';
                      const desclass = r.situacaoUltimaNegociacao === 'D' || r.desclassificado;
                      return (
                        <tr key={i} className={`border-b border-slate-800 ${isMine ? 'bg-emerald-500/10 border-emerald-700' : ''}`}>
                          <td className={`px-3 py-2 text-center font-bold ${cls === 1 ? 'text-amber-400' : cls <= 3 ? 'text-blue-400' : isMine ? 'text-emerald-400' : 'text-slate-400'}`}>{cls}º</td>
                          <td className="px-3 py-2">
                            <div className={`text-xs ${isMine ? 'text-emerald-300 font-semibold' : 'text-slate-200'}`}>{nome || '—'}</div>
                            <div className="text-xs text-slate-500 font-mono">{cnpj || '—'} {uf ? `· ${uf}` : ''}</div>
                            {desclass && <span className="text-xs text-red-400">desclassificado</span>}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-500 font-mono text-xs">{fmtCurrency4(valIni)}</td>
                          <td className={`px-3 py-2 text-right font-mono ${isMine ? 'text-emerald-300 font-bold' : 'text-slate-100'}`}>{fmtCurrency4(valFin)}</td>
                          <td className="px-3 py-2 text-center text-xs">{r.declaracaoMeEpp ? <span className="text-emerald-400">✓</span> : <span className="text-slate-600">—</span>}</td>
                        </tr>
                      );
                    }
                    // Schemas reais (HAR 2026-05-19):
                    // por-participante / lances: { valor: { valorCalculado, valorInformado }, origem: 'P'|'L', dataHoraInclusao, excluido }
                    // propostas-iniciais: { quantidadeOfertada, valorUnitario, descricao, ... }
                    const valor = r.valor?.valorCalculado ?? r.valor?.valorInformado ?? r.valorUnitario ?? r.valorLance ?? r.valorTotal;
                    const data = r.dataHoraInclusao ?? r.dataHora ?? r.dataRegistro ?? r.timestamp;
                    const origemRaw = r.origem ?? r.tipo;
                    const origem = origemRaw === 'P' ? 'Proposta' : origemRaw === 'L' ? 'Lance' : origemRaw;
                    const qtd = r.quantidadeOfertada ?? r.quantidade ?? r.qtd;
                    const isMine =
                      r.minha ?? r.ehMinha ?? (r.participante?.identificacao === '06163889000104' || ehMeuValor(r.valor?.valorCalculado ?? r.valor?.valorInformado));
                    const excluido = r.excluido === true;
                    return (
                      <tr key={i} className={`border-b border-slate-800 ${isMine ? 'bg-emerald-500/5' : ''}`}>
                        {tab === 'propostas' ? (
                          propostasIsAggregate ? (() => {
                            const cnpj = r.participante?.identificacao;
                            const nome = r.participante?.nome;
                            const uf = r.participante?.endereco?.uf;
                            const valTotalIni = r.valores?.valorPropostaInicial?.valorCalculado?.valorTotal
                              ?? r.valores?.valorPropostaInicial?.valorInformado
                              ?? r.valorTotal
                              ?? r.valorPropostaInicial?.valorCalculado?.valorTotal;
                            const idxInGroup = page * PAGE_SIZE + i + 1;
                            const isMineCell = cnpj === '06163889000104';
                            return (
                              <>
                                <td className="px-3 py-2 text-center text-slate-400">{idxInGroup}</td>
                                <td className="px-3 py-2">
                                  <div className={`text-xs ${isMineCell ? 'text-emerald-300 font-semibold' : 'text-slate-200'}`}>{nome || `Proposta ${idxInGroup}`}</div>
                                  {cnpj && <div className="text-xs text-slate-500 font-mono">{cnpj}{uf ? ` · ${uf}` : ''}</div>}
                                </td>
                                <td className={`px-3 py-2 text-right font-mono ${isMineCell ? 'text-emerald-300 font-bold' : 'text-slate-100'}`}>{fmtCurrency4(valTotalIni)}</td>
                              </>
                            );
                          })() : (
                            <>
                              <td className="px-3 py-2 text-slate-300">{r.nome || r.proposta || `Proposta ${page * PAGE_SIZE + i + 1}`}</td>
                              <td className="px-3 py-2 text-right text-slate-400 font-mono">{qtd ?? '—'}</td>
                              <td className="px-3 py-2 text-right text-slate-100 font-mono">{fmtCurrency4(valor)}</td>
                            </>
                          )
                        ) : (
                          <>
                            <td className="px-3 py-2 text-slate-400 text-xs font-mono">
                              {isMine && <span className="mr-1 text-emerald-400 font-semibold">IPCOM</span>}
                              {fmtDateTime(data)}
                            </td>
                            <td className={`px-3 py-2 text-right font-mono ${isMine ? 'text-emerald-400 font-bold' : 'text-slate-100'}`}>
                              {fmtCurrency4(valor)}
                            </td>
                            <td className="px-3 py-2 text-slate-400 text-xs">{origem ?? '—'}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4 text-sm">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1 rounded bg-central-cartao disabled:opacity-30 hover:bg-central-elevado"
                  >Anterior</button>
                  <span className="text-slate-400">{page + 1} / {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1 rounded bg-central-cartao disabled:opacity-30 hover:bg-central-elevado"
                  >Próxima</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
